import { useEffect, useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import LicenseInvoiceSection from "@/components/LicenseInvoiceSection";
import {
  hasActiveCommercialLicense,
  moduleIdsForLicenseRequest,
  moduleIdsForTrial,
  moduleLabel,
  resolveActiveModuleIds,
  resolveCommercialModuleCatalog,
  resolveRequestableModuleIds,
} from "@/lib/commercialLicenseRequests";
import {
  defaultRequestedLicenseType,
  requestedLicenseTypeOptions,
} from "@/lib/licenseTypes";
import { fetchSystemCommercialPlans } from "@/lib/commercialLicenseRequestsApi";
import {
  commercialPlanModuleIds,
  commercialPlanOptions,
} from "@/lib/commercialPlanUi";
import { isTrialActive } from "@/modules/panelHubOnboarding";

function SectionBlock({ title, children, testId }) {
  return (
    <section className="rounded-sm border border-slate-200 bg-slate-50/40 p-3 space-y-2" data-testid={testId}>
      <h5 className="text-xs font-semibold text-[#0F2D5C] m-0">{title}</h5>
      {children}
    </section>
  );
}

/**
 * Selección compartida de módulos, tipo de licencia y factura (antes del método web/PDF).
 */
export default function LicenseCommercialSharedForm({
  form,
  setForm,
  systemInfo,
  profile = {},
  disabled = false,
  onApplyRegistrationInvoiceData,
  hideTrialModules = false,
}) {
  const catalog = useMemo(() => resolveCommercialModuleCatalog(systemInfo), [systemInfo]);
  const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
  const trialActive = isTrialActive(systemInfo?.subscription || {});
  const activeModuleIds = useMemo(() => resolveActiveModuleIds(systemInfo), [systemInfo]);
  const requestableModuleIds = useMemo(() => resolveRequestableModuleIds(systemInfo), [systemInfo]);
  const licenseTypeOptions = useMemo(
    () => requestedLicenseTypeOptions(hasActiveLicense),
    [hasActiveLicense],
  );
  const requestModuleIds = moduleIdsForLicenseRequest(catalog);
  const moduleIds = hasActiveLicense ? requestableModuleIds : requestModuleIds;
  const allModulesActive = hasActiveLicense && requestableModuleIds.length === 0;
  const [plansCatalog, setPlansCatalog] = useState(null);
  const planOptions = useMemo(() => commercialPlanOptions(plansCatalog), [plansCatalog]);
  const usePlanSelection = !hasActiveLicense && planOptions.length > 0;

  useEffect(() => {
    if (hasActiveLicense) return undefined;
    let cancelled = false;
    fetchSystemCommercialPlans()
      .then((data) => {
        if (!cancelled) {
          const catalog = { plans: Object.fromEntries((data?.items || []).map((plan) => [plan.id, plan])) };
          const options = commercialPlanOptions(catalog);
          setPlansCatalog(catalog);
          setForm((prev) => {
            const selected = options.find((plan) => plan.id === prev.commercial_plan) || options[0];
            if (!selected) return prev;
            return {
              ...prev,
              commercial_plan: selected.id,
              requested_modules: { ...prev.requested_modules, ...selected.entitlements },
            };
          });
        }
      })
      .catch(() => {
        if (!cancelled) setPlansCatalog(null);
      });
    return () => {
      cancelled = true;
    };
  }, [hasActiveLicense, setForm]);

  const choosePlan = (plan) => {
    setForm((prev) => ({
      ...prev,
      commercial_plan: plan.id,
      billing_cadence: prev.billing_cadence || "annual",
      requested_modules: { ...prev.requested_modules, ...plan.entitlements },
    }));
  };

  const toggleModule = (moduleId, checked) => {
    setForm((prev) => ({
      ...prev,
      requested_modules: { ...prev.requested_modules, [moduleId]: checked },
    }));
  };

  const trialModuleLabels = moduleIdsForTrial(catalog).map((id) => moduleLabel(id, catalog));

  return (
    <div className="space-y-4" data-testid="license-commercial-shared-form">
      {trialActive && !hideTrialModules ? (
        <SectionBlock title="Módulos disponibles en tu periodo de prueba" testId="license-commercial-trial-modules">
          <ul className="m-0 pl-4 text-[11px] text-slate-700 space-y-1 list-disc">
            {trialModuleLabels.map((label) => (
              <li key={label}>{label}</li>
            ))}
          </ul>
          <p className="text-[10px] text-slate-500 m-0">
            Durante la prueba puedes usar estos módulos. Indique abajo cuáles desea contratar al solicitar licencia.
          </p>
        </SectionBlock>
      ) : activeModuleIds.length > 0 ? (
        <SectionBlock title="Módulos activos en tu licencia" testId="license-commercial-active-modules">
          <p className="text-[11px] text-slate-700 m-0">
            {activeModuleIds.map((id) => moduleLabel(id, catalog)).join(", ")}
          </p>
        </SectionBlock>
      ) : null}

      {usePlanSelection ? (
        <SectionBlock title="Plan que desea contratar" testId="license-commercial-plan-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {planOptions.map((plan) => (
              <label
                key={plan.id}
                className={`cursor-pointer rounded-sm border px-3 py-2 text-[11px] ${
                  form.commercial_plan === plan.id
                    ? "border-[#0F2D5C] bg-[#0F2D5C]/5"
                    : "border-slate-200 bg-white hover:bg-slate-50"
                }`}
                data-testid={`license-commercial-plan-${plan.id}`}
              >
                <span className="flex items-center gap-2 font-semibold text-[#0F2D5C]">
                  <input
                    type="radio"
                    name="commercial-plan"
                    checked={form.commercial_plan === plan.id}
                    onChange={() => choosePlan(plan)}
                    disabled={disabled}
                  />
                  {plan.label}
                </span>
                <span className="block mt-1 pl-5 text-slate-600">
                  {commercialPlanModuleIds(plan).map((id) => moduleLabel(id, catalog)).join(" · ")}
                </span>
                <span className="block mt-1 pl-5 text-slate-500">
                  Hasta {plan.limits?.authorized_users ?? "—"} usuarios · {plan.limits?.authorized_clients ?? "—"} clientes
                </span>
              </label>
            ))}
          </div>
          <div className="mt-2 max-w-md">
            <Label className="text-xs">Cadencia de cobro</Label>
            <Select
              value={form.billing_cadence || "annual"}
              onValueChange={(value) => setForm((prev) => ({ ...prev, billing_cadence: value }))}
              disabled={disabled}
            >
              <SelectTrigger className="mt-1 h-9 text-xs" data-testid="license-commercial-billing-cadence">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly" className="text-xs">Mensual</SelectItem>
                <SelectItem value="annual" className="text-xs">Anual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-[10px] text-slate-500 m-0">
            Los módulos incluidos y los límites se determinan por el plan publicado; BNT confirmará vigencia y pago.
          </p>
        </SectionBlock>
      ) : !allModulesActive ? (
        <SectionBlock title="Módulos que desea contratar (flujo legacy)" testId="license-commercial-modules-section">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {moduleIds.map((moduleId) => (
              <label
                key={moduleId}
                className="flex items-start gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-[11px] cursor-pointer hover:bg-slate-50"
                data-testid={`license-commercial-module-${moduleId}`}
              >
                <Checkbox
                  checked={Boolean(form.requested_modules?.[moduleId])}
                  onCheckedChange={(v) => toggleModule(moduleId, v === true)}
                  disabled={disabled}
                />
                <span>{moduleLabel(moduleId, catalog)}</span>
              </label>
            ))}
          </div>
          <p className="text-[10px] text-slate-500 m-0">
            {trialActive && !hideTrialModules
              ? `Durante la prueba puedes usar los ${trialModuleLabels.length} módulos; aquí selecciona solo los módulos que deseas contratar.`
              : "Seleccione al menos un módulo para continuar."}
          </p>
        </SectionBlock>
      ) : (
        <p className="text-[11px] text-slate-600 m-0" data-testid="license-commercial-all-modules-active">
          Su licencia ya incluye todos los módulos principales. Puede solicitar renovación o ampliación en comentarios
          del método elegido.
        </p>
      )}

      <SectionBlock title="Tipo de licencia solicitado" testId="license-commercial-type-section">
        <div className="max-w-md">
          <Select
            value={form.requested_license_type || defaultRequestedLicenseType(hasActiveLicense)}
            onValueChange={(v) => setForm((f) => ({ ...f, requested_license_type: v }))}
            disabled={disabled}
          >
            <SelectTrigger className="h-9 text-xs" data-testid="license-commercial-type-select">
              <SelectValue placeholder="Seleccione tipo de licencia" />
            </SelectTrigger>
            <SelectContent>
              {licenseTypeOptions.map((opt) => (
                <SelectItem key={opt.id} value={opt.id} className="text-xs">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500 mt-1 m-0">
            BNT confirmará el tipo y la vigencia al autorizar. Esto no activa módulos por sí solo.
          </p>
        </div>
      </SectionBlock>

      <SectionBlock title="Facturación" testId="license-commercial-invoice-section">
        <LicenseInvoiceSection
          form={form}
          setForm={setForm}
          profile={profile}
          disabled={disabled}
          onApplyRegistrationData={onApplyRegistrationInvoiceData}
          showModuleSummary
          invoiceQuestion="¿Requiere factura?"
          testId="license-commercial-invoice-fields"
        />
      </SectionBlock>
    </div>
  );
}
